import { nowAppIso } from '@/lib/dayjs';
import {
  getItem,
  setItem,
  removeItem,
  broadcastStorageChange,
  dispatchStorageSignal,
} from '@/lib/storage';
import {
  OfflineOperation,
  OfflineOperationType,
  OfflineOperationStatus,
} from '@/types/offlineQueue';

// Offline queue plan:
// - Persist mutations (create/update/delete) while offline using localforage so they survive reloads.
// - Broadcast queue changes via a lightweight localStorage toggle for cross-tab updates.
// - Expose helpers to enqueue operations, update their status, prune completed ones, and subscribe from UI/hooks.

const QUEUE_KEY = 'offlineQueue:v1';
const SIGNAL_KEY = 'wsc:offline-queue:signal';

const generateId = () => {
  if (
    typeof crypto !== 'undefined' &&
    'randomUUID' in crypto &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  return `op-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

const readQueue = async (): Promise<OfflineOperation[]> => {
  try {
    const stored = await getItem<OfflineOperation[]>(QUEUE_KEY);
    if (!stored || !Array.isArray(stored)) {
      return [];
    }
    return stored
      .filter((op) => op && typeof op === 'object')
      .map((op) => ({
        ...op,
        attempts: typeof op.attempts === 'number' ? op.attempts : 0,
        status: op.status ?? 'pending',
        updatedAt: op.updatedAt ?? op.createdAt ?? nowAppIso(),
      }))
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  } catch (e) {
    return [];
  }
};

const writeQueue = async (queue: OfflineOperation[]) => {
  await setItem(QUEUE_KEY, queue);
  if (typeof window !== 'undefined') {
    broadcastStorageChange(SIGNAL_KEY);
    dispatchStorageSignal(SIGNAL_KEY);
  }
};

export async function loadQueue(): Promise<OfflineOperation[]> {
  return readQueue();
}

export async function enqueueOperation(input: {
  entityType: string;
  entityId: string;
  type: OfflineOperationType;
  payload: Record<string, any>;
}): Promise<OfflineOperation> {
  const now = nowAppIso();
  const op: OfflineOperation = {
    id: generateId(),
    entityType: input.entityType,
    entityId: input.entityId,
    type: input.type,
    payload: input.payload,
    status: 'pending',
    attempts: 0,
    createdAt: now,
    updatedAt: now,
  };

  const queue = await readQueue();
  queue.push(op);
  await writeQueue(queue);
  return op;
}

export async function updateOperationStatus(
  id: string,
  status: OfflineOperationStatus,
  options?: { error?: string; incrementAttempt?: boolean }
): Promise<OfflineOperation | undefined> {
  const queue = await readQueue();
  const idx = queue.findIndex((op) => op.id === id);
  if (idx === -1) {
    return undefined;
  }
  const now = nowAppIso();
  const existing = queue[idx];
  const updated: OfflineOperation = {
    ...existing,
    status,
    error: options?.error,
    attempts: options?.incrementAttempt ? existing.attempts + 1 : existing.attempts,
    updatedAt: now,
  };
  queue[idx] = updated;
  await writeQueue(queue);
  return updated;
}

export async function removeOperation(id: string): Promise<void> {
  const queue = await readQueue();
  const next = queue.filter((op) => op.id !== id);
  await writeQueue(next);
}

export async function clearQueue(): Promise<void> {
  await removeItem(QUEUE_KEY);
  if (typeof window !== 'undefined') {
    broadcastStorageChange(SIGNAL_KEY);
    dispatchStorageSignal(SIGNAL_KEY);
  }
}

export type QueueSubscriber = (queue: OfflineOperation[]) => void;

export function subscribeQueue(cb: QueueSubscriber): () => void {
  if (typeof window === 'undefined') {
    return () => {};
  }

  const handleUpdate = async () => {
    const next = await readQueue();
    cb(next);
  };

  const storageHandler = (e: StorageEvent) => {
    if (e.key !== SIGNAL_KEY) {
      return;
    }
    handleUpdate();
  };

  window.addEventListener('storage', storageHandler);
  window.addEventListener(SIGNAL_KEY, handleUpdate as EventListener);

  return () => {
    window.removeEventListener('storage', storageHandler);
    window.removeEventListener(SIGNAL_KEY, handleUpdate as EventListener);
  };
}
